import type {
  CreateSessionInput,
  CreateSessionResponse,
  PrivacyAcceptanceInput,
  PrivacyAcceptanceResponse,
  SessionResponse,
  UpdateSessionInput,
  UpdateSessionResponse,
} from '@/shared/types/api';
import { api, callBreadcrumbs, instrument, unwrap } from '../client';

export function create(
  data: CreateSessionInput = {}
): Promise<CreateSessionResponse> {
  return instrument<CreateSessionResponse>(
    {
      span: 'api.create_session',
      errorTag: 'sessions_create_session',
      tags: { 'api.source': data.source },
      before: {
        message: 'Creating new session',
        data: { source: data.source },
      },
      after: (r) => ({
        message: 'Session created successfully',
        // Full session IDs are required for Sentry-to-Postgres correlation.
        data: { session_id: r?.session_id },
      }),
      errorExtra: { request_data: { source: data.source } },
      trackErrorCode: 'api_error_session',
    },
    () => unwrap(api.POST('/v1/sessions', { body: data }))
  );
}

export function getById(sessionId: string): Promise<SessionResponse> {
  return instrument<SessionResponse>(
    {
      span: 'api.get_session',
      errorTag: 'sessions_get_session',
      tags: { 'session.id': sessionId },
      // Preserve full session IDs for observability.
      ...callBreadcrumbs('Fetching session by ID', 'Session fetched', {
        session_id: sessionId,
      }),
      // 404 is expected (expired/wrong download code) — don't noise up Sentry.
      skipSentryOn: [404],
      trackErrorCode: 'api_error_get_session_by_id',
    },
    () =>
      unwrap(
        api.GET('/v1/sessions/{session_id}', {
          params: { path: { session_id: sessionId } },
        })
      )
  );
}

export function recordPrivacyAcceptance(
  sessionId: string,
  data: PrivacyAcceptanceInput = {}
): Promise<PrivacyAcceptanceResponse> {
  return instrument<PrivacyAcceptanceResponse>(
    {
      span: 'api.record_privacy_acceptance',
      errorTag: 'sessions_record_privacy_acceptance',
      tags: { 'session.id': sessionId },
      ...callBreadcrumbs(
        'Recording privacy acceptance',
        'Privacy acceptance recorded',
        { session_id: sessionId }
      ),
      trackErrorCode: 'api_error_record_privacy_acceptance',
    },
    () =>
      unwrap(
        api.POST('/v1/sessions/{session_id}/privacy-acceptance', {
          params: { path: { session_id: sessionId } },
          body: data,
        })
      )
  );
}

export function updateById(
  sessionId: string,
  data: UpdateSessionInput
): Promise<UpdateSessionResponse> {
  const fields = Object.keys(data);
  return instrument<UpdateSessionResponse>(
    {
      span: 'api.update_session',
      errorTag: 'sessions_update_session',
      tags: { 'session.id': sessionId, 'api.fields': fields.join(',') },
      // Preserve full session IDs for observability.
      ...callBreadcrumbs('Updating session', 'Session updated', {
        session_id: sessionId,
        fields,
      }),
      trackErrorCode: 'api_error_update_session_by_id',
    },
    () =>
      unwrap(
        api.PATCH('/v1/sessions/{session_id}', {
          params: { path: { session_id: sessionId } },
          body: data,
        })
      )
  );
}
